import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Typography,
  Button,
  Paper,
  TextField,
  Container,
  Card,
  CardContent,
} from '@mui/material';
import { useContent } from '../context/ContentContext';
import ErrorScreen from '../components/ErrorScreen';
import { useState } from 'react';
import brand from '../theme/brand';

const LoadingScreen: React.FC = () => {
  const { viewState, loadFormById } = useContent();
  const navigate = useNavigate();
  const location = useLocation();
  const [formId, setFormId] = useState('');

  // On initial load, try to extract parameters from URL
  useEffect(() => {
    // First, check if there are any URL parameters
    const params = new URLSearchParams(location.search);
    const formParam = params.get('form');
    
    // Check for alternate parameter formats (formId)
    const formIdParam = params.get('formId');
    
    // Check URL path segments for form IDs
    const pathSegments = location.pathname.split('/').filter(segment => segment);
    console.log('URL path segments:', pathSegments);
    console.log('URL parameters:', { formParam, formIdParam });
    
    if (pathSegments.length >= 2) {
      const type = pathSegments[0];
      const id = pathSegments[1];
      
      if (type === 'form' && id) {
        console.log('Found form ID in path:', id);
        const processForm = async () => {
          try {
            const form = await loadFormById(id);
            if (form) {
              navigate(`/form/${form.id}`);
              return;
            }
          } catch (error) {
            console.error('Error loading form from path:', error);
          }
        };
        processForm();
        return;
      }
    }
    
    // Try to find a form ID in the URL path or query parameters
    const processUrl = async () => {
      try {
        console.log('Processing URL parameters:', { formParam, formIdParam });
        
        // Case 1: Direct URL parameter
        if (formParam) {
          console.log('Loading form by ID (from form param):', formParam);
          const form = await loadFormById(formParam);
          console.log('Form loaded:', form);
          if (form) {
            navigate(`/form/${form.id}`);
            return;
          }
        }
        
        // Handle formId parameter as an alternative
        if (formIdParam) {
          console.log('Loading form by ID (from formId param):', formIdParam);
          // Clean up the ID if it contains a question mark (common in incorrectly formed URLs)
          const cleanId = formIdParam.split('?')[0];
          const form = await loadFormById(cleanId);
          console.log('Form loaded from formId param:', form);
          if (form) {
            navigate(`/form/${form.id}`);
            return;
          }
        }
        
        // Case 2: Parse the full URL to find form IDs
        // This handles cases where the entire URL is passed
        try {
          const urlString = location.search.substring(1); // Remove the ? character
          if (urlString && urlString.includes('=')) {
            const url = new URL(decodeURIComponent(urlString));
            const urlParams = new URLSearchParams(url.search);
            
            const extractedFormId = urlParams.get('form');
            
            if (extractedFormId) {
              const form = await loadFormById(extractedFormId);
              if (form) {
                navigate(`/form/${form.id}`);
                return;
              }
            }
          }
        } catch (error) {
          console.error('Error parsing URL:', error);
        }
      } catch (error) {
        console.error('Error processing URL:', error);
      }
    };

    processUrl();
  }, [location.search, loadFormById, navigate]);

  // Handle form submission
  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submission with ID:', formId);
    
    if (formId.trim()) {
      try {
        // Try to load the form using the direct ID
        console.log('Attempting to load form by ID:', formId.trim());
        const form = await loadFormById(formId.trim());
        console.log('Form load result:', form);
        
        if (form) {
          console.log('Form loaded successfully, navigating to:', form.id);
          navigate(`/form/${form.id}`);
          return;
        }
        
        // If that fails, check if it's a Form ID with a prefix
        if (formId.includes('Public_Form_')) {
          const cleanId = formId.trim();
          console.log('Attempting to load form with Public_Form_ prefix:', cleanId);
          const form = await loadFormById(cleanId);
          console.log('Form load result (with prefix):', form);
          
          if (form) {
            console.log('Form loaded successfully, navigating to:', form.id);
            navigate(`/form/${form.id}`);
            return;
          }
        }
        
        // If still no success, try without the prefix if it has one
        if (formId.includes('Public_Form_')) {
          const cleanId = formId.replace('Public_Form_', '').trim();
          console.log('Attempting to load form without Public_Form_ prefix:', cleanId);
          const form = await loadFormById(cleanId);
          console.log('Form load result (without prefix):', form);
          
          if (form) {
            console.log('Form loaded successfully, navigating to:', form.id);
            navigate(`/form/${form.id}`);
            return;
          }
        }
        
        console.log('Failed to load form with ID:', formId);
      } catch (error) {
        console.error('Error loading form:', error);
      }
    }
  };

  // Whether the URL actually references content to load. Without this, the
  // initial LOADING view state would spin forever on a direct visit and the
  // entry form below would never render.
  const urlParams = new URLSearchParams(location.search);
  const firstSegment = location.pathname.split('/').filter(Boolean)[0] || '';
  const hasContentRequest = Boolean(
    urlParams.get('form') ||
    urlParams.get('formId') ||
    firstSegment === 'form'
  );

  // Show loading indicator
  if (viewState.type === 'LOADING' && hasContentRequest) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          backgroundColor: 'background.default',
        }}
      >
        <CircularProgress size={52} thickness={3.5} sx={{ mb: 4, color: 'primary.main' }} />
        <Typography variant="h4" sx={{ mb: 1, color: 'primary.main' }}>
          One moment…
        </Typography>
        <Typography variant="body1" color="text.secondary" align="center">
          We're getting your content ready.
        </Typography>
      </Box>
    );
  }

  // Show entry form if no content is loaded
  if ((viewState.type as string) !== 'FORM_LOADED') {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            py: 4,
          }}
        >
          <Typography
            variant="h2"
            component="h1"
            sx={{
              color: 'primary.main',
              textAlign: 'center',
              mb: 1,
              fontSize: 'clamp(2.4rem, 8vw, 3.2rem)',
            }}
          >
            Paiper
            <Box component="span" sx={{ color: 'secondary.main' }}>.</Box>
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ textAlign: 'center', mb: 4, maxWidth: 380 }}
          >
            Open a form — paste the link or ID a business shared with you.
          </Typography>

          <Card
            sx={{
              width: '100%',
              overflow: 'visible',
              mb: 4,
            }}
          >
            <CardContent sx={{ p: 0 }}>
              {(
                <Box sx={{ p: 3 }}>
                  <form onSubmit={handleFormSubmit}>
                    <Typography variant="subtitle1" gutterBottom>
                      Enter a form ID or paste a form link:
                    </Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={formId}
                      onChange={(e) => setFormId(e.target.value)}
                      placeholder="Form ID or URL"
                      sx={{ mb: 2 }}
                    />
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      type="submit"
                      size="large"
                      sx={{ borderRadius: 3, py: 1.5, background: brand.forestGradient, '&.Mui-disabled': { background: 'rgba(0, 0, 0, 0.12)' } }}
                      disabled={!formId.trim()}
                    >
                      Go to Form
                    </Button>
                  </form>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  // Handle different view states
  if (viewState.type === 'FORM_NOT_FOUND') {
    return (
      <ErrorScreen
        title="Form Not Found"
        message="The form you're looking for may have been deleted or is unavailable."
        buttonText="Go Back"
        onButtonClick={() => navigate('/')}
      />
    );
  }
  
  if (viewState.type === 'NETWORK_ERROR') {
    return (
      <ErrorScreen
        title="Network Error"
        message="Unable to connect to the server. Please check your internet connection and try again."
        buttonText="Retry"
        onButtonClick={() => {
          if (formId.trim()) {
            loadFormById(formId.trim());
          }
        }}
      />
    );
  }
  
  if (viewState.type === 'ERROR') {
    return (
      <ErrorScreen
        title="Error"
        message={viewState.message}
        buttonText="Go Back"
        onButtonClick={() => navigate('/')}
      />
    );
  }
  
  // If we get here, we have a FORM_LOADED state that should have been
  // redirected already, or some unexpected state. Show a generic loading message
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
      }}
    >
      <Typography variant="h5" sx={{ mb: 2 }}>
        Loading...
      </Typography>
    </Box>
  );
};

export default LoadingScreen;